<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <div
      ref="container"
      class="screen__stretch pretty-scrollbar"
      style="padding-bottom: 6em;"
    >
      <BaseForm
        ref="form"
        v-bind="form"
        class="screen__form"
        @submit="onFormSubmit"
      />
    </div>
  </BaseScreen>
</template>

<script>
import { mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import formMixin from '@/mixins/form';

import FieldSelect from '@/components/FieldSelect.vue';

const FIELDS = Object.freeze({
  ORGANIZATION: 'organization',
  CITY: 'city',
  STREET: 'street',
  HOUSE: 'house',
  PREMISE: 'premise',
  ACCOUNT: 'account',
});

const ITEMS = Object.freeze({
  ORGANIZATIONS: 'organizations',
  CITIES: 'cities',
  STREETS: 'streets',
  HOUSES: 'houses',
  PREMISES: 'premises',
  ACCOUNTS: 'accounts',
});

export default {
  name: 'PersonalAddAccountScreen',
  mixins: [screenMixin, formMixin],
  data() {
    return {
      values: {
        [FIELDS.ORGANIZATION]: null,
        [FIELDS.CITY]: null,
        [FIELDS.STREET]: null,
        [FIELDS.HOUSE]: null,
        [FIELDS.PREMISE]: null,
        [FIELDS.ACCOUNT]: null,
      },
      [ITEMS.ORGANIZATIONS]: [],
      [ITEMS.CITIES]: [],
      [ITEMS.STREETS]: [],
      [ITEMS.HOUSES]: [],
      [ITEMS.PREMISES]: [],
      [ITEMS.ACCOUNTS]: [],
    };
  },
  computed: {
    titleText() {
      return this.$t('titles.addAccount');
    },
    pendingText() {
      return this.organizations.length
        ? this.$t('pending.account')
        : this.$t('pending.fetch');
    },
    screenCase() {
      if (this.isThanksVisible) {
        return CONSTANTS.STEP_ACCOUNT_ADDED;
      }
      if (this.isErrorVisible) {
        return this.isAccountFinded
          ? CONSTANTS.STEP_ADD_FAILED
          : CONSTANTS.STEP_CONNECTION_ERROR;
      }
      if (this.organizations.length) {
        return CONSTANTS.STEP_ADD_ACCOUNT;
      }
      return null;
    },
    retryAction() {
      return this.isAccountFinded ? this.onFormSubmit : null;
    },
    isFormHasValue() {
      return Object.values(this.values).filter(Boolean).length > 0;
    },
    isAccountFinded() {
      return Boolean(this.account);
    },
    account() {
      const selectedAccount = this.values[FIELDS.ACCOUNT];
      const [firstAccount] = this.accounts;
      return this.accounts.length === 1 ? firstAccount : selectedAccount;
    },
    cityName() {
      return this.values[FIELDS.CITY]?.label;
    },
    streetName() {
      return this.values[FIELDS.STREET]?.label;
    },
    houseName() {
      return this.values[FIELDS.HOUSE]?.label;
    },
    form() {
      return {
        values: this.values,
        fields: [
          {
            is: FieldSelect,
            name: FIELDS.ORGANIZATION,
            label: this.$t('fields.organizationLabel'),
            placeholder: this.$t('fields.organizationPlaceholder'),
            searchPlaceholder: this.$t('fields.organizationSearchPlaceholder'),
            emptyPlaceholder: this.$t('fields.organizationEmptyPlaceholder'),
            errorMessage: this.$t('fields.organizationError'),
            options: this[ITEMS.ORGANIZATIONS],
            isVisible: true,
          },
          {
            is: FieldSelect,
            name: FIELDS.CITY,
            label: this.$t('fields.cityLabel'),
            placeholder: this.$t('fields.cityPlaceholder'),
            searchPlaceholder: this.$t('fields.citySearchPlaceholder'),
            emptyPlaceholder: this.$t('fields.cityEmptyPlaceholder'),
            errorMessage: this.$t('fields.cityError'),
            options: this[ITEMS.CITIES],
            isVisible: Boolean(this.values[FIELDS.ORGANIZATION]),
          },
          {
            is: FieldSelect,
            name: FIELDS.STREET,
            label: this.$t('fields.streetLabel'),
            placeholder: this.$t('fields.streetPlaceholder'),
            searchPlaceholder: this.$t('fields.streetSearchPlaceholder'),
            emptyPlaceholder: this.$t('fields.streetEmptyPlaceholder', { city: this.cityName }),
            errorMessage: this.$t('fields.streetError'),
            options: this[ITEMS.STREETS],
            isVisible: Boolean(this.values[FIELDS.CITY]),
          },
          {
            is: FieldSelect,
            name: FIELDS.HOUSE,
            label: this.$t('fields.houseLabel'),
            placeholder: this.$t('fields.housePlaceholder'),
            searchPlaceholder: this.$t('fields.houseSearchPlaceholder'),
            emptyPlaceholder: this.$t('fields.houseEmptyPlaceholder', { street: this.streetName }),
            errorMessage: this.$t('fields.houseError'),
            options: this[ITEMS.HOUSES],
            isVisible: Boolean(this.values[FIELDS.STREET]),
          },
          {
            is: FieldSelect,
            name: FIELDS.PREMISE,
            label: this.$t('fields.premiseLabel'),
            placeholder: this.$t('fields.premisePlaceholder'),
            searchPlaceholder: this.$t('fields.premiseSearchPlaceholder'),
            emptyPlaceholder: this.$t('fields.premiseEmptyPlaceholder', { house: this.houseName }),
            errorMessage: this.$t('fields.premiseError'),
            options: this[ITEMS.PREMISES],
            isVisible: Boolean(this.values[FIELDS.HOUSE]),
          },
          {
            is: FieldSelect,
            name: FIELDS.ACCOUNT,
            label: this.$t('fields.accountLabel'),
            placeholder: this.$t('fields.accountPlaceholder'),
            errorMessage: this.$t('fields.accountError'),
            options: this[ITEMS.ACCOUNTS],
            isVisible: Boolean(this.values[FIELDS.PREMISE]),
          },
        ].filter((field) => field.isVisible),
        actions: [
          this.isFormHasValue ? {
            text: this.$t('actions.clear'),
            action: this.clearValues,
            view: 'outlined',
          } : null,
          this.isAccountFinded ? {
            type: 'submit',
            text: this.$t('actions.addAccount'),
          } : null,
        ].filter(Boolean),
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          size: 'l',
        },
        description: {
          text: this.$t('texts.accountWasAdded'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.addMore'),
            action: this.addMore,
            view: 'outlined',
          },
          {
            text: this.$t('actions.backToPersonal'),
            action: this.goToPersonalScreen,
          },
        ],
      };
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badConnection'),
          size: 'l',
        },
        description: {
          text: this.$t(this.isAccountFinded
            ? 'texts.addAccountFailed'
            : 'texts.badConnection'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryErrorAction,
          },
        ],
      };
    },
  },
  watch: {
    [`values.${FIELDS.ORGANIZATION}`]: 'fetchCities',
    [`values.${FIELDS.CITY}`]: 'fetchStreets',
    [`values.${FIELDS.STREET}`]: 'fetchHouses',
    [`values.${FIELDS.HOUSE}`]: 'fetchPremises',
    [`values.${FIELDS.PREMISE}`]: 'fetchAccounts',
  },
  created() {
    this.initActions([
      [CONSTANTS.ACTION_BACK, this.goToPersonalScreen],
      [CONSTANTS.ACTION_MORE, this.addMore],
      [CONSTANTS.ACTION_CANCEL, this.clearValues],
    ]);
  },
  mounted() {
    this.prefetchOrganizations();
  },
  methods: {
    ...mapActions('personal', [
      'fetchProfileAccounts',
      'fetchProfileCharges',
      'fetchOrganizations',
      'fetchCitiesByOrganizationId',
      'fetchStreetsByCityId',
      'fetchHousesByStreetId',
      'fetchPremisesByHouseId',
      'fetchAccountsByPremiseId',
      'bindAccountById',
    ]),
    async onFormSubmit() {
      try {
        this.showPending();
        await this.bindAccountById(this.account.id);
        await Promise.all([
          this.fetchProfileAccounts(),
          this.fetchProfileCharges(),
        ]);
        this.showThanks();
      } catch (e) {
        if (e.msg) {
          this.sayText(e.msg);
          this.$refs.form?.setMessage(e.msg);
          this.$refs.container.scrollTop = 0;
          this.hidePending();
        } else {
          this.showError();
        }
        console.error('onFormSubmit', e);
      }
    },
    async fetchItems(action, itemsName, fieldName) {
      try {
        this.clearFormMessage();
        this.clearValueByName(fieldName);

        await this.$nextTick();

        this.scrollContainerToBottom();
        this.showSpinnerByFieldName(fieldName);

        this[itemsName] = await action();

        this.hideSpinnerByFieldName(fieldName);
        this.setFocusByFieldName(fieldName);
      } catch (e) {
        console.error(e);
        this.showError();
      }
    },
    prefetchOrganizations() {
      const action = () => this.fetchOrganizations();
      this.fetchItems(action, ITEMS.ORGANIZATIONS, FIELDS.ORGANIZATION);
    },
    fetchCities(organization) {
      const action = () => (organization
        ? this.fetchCitiesByOrganizationId(organization.id)
        : Promise.resolve([]));
      this.fetchItems(action, ITEMS.CITIES, FIELDS.CITY);
    },
    fetchStreets(city) {
      const action = () => (city
        ? this.fetchStreetsByCityId(city.id)
        : Promise.resolve([]));
      this.fetchItems(action, ITEMS.STREETS, FIELDS.STREET);
    },
    fetchHouses(street) {
      const action = () => (street
        ? this.fetchHousesByStreetId(street.id)
        : Promise.resolve([]));
      this.fetchItems(action, ITEMS.HOUSES, FIELDS.HOUSE);
    },
    fetchPremises(house) {
      const action = () => (house
        ? this.fetchPremisesByHouseId(house.id)
        : Promise.resolve([]));
      this.fetchItems(action, ITEMS.PREMISES, FIELDS.PREMISE);
    },
    async fetchAccounts(premise) {
      const action = () => (premise
        ? this.fetchAccountsByPremiseId(premise.id)
        : Promise.resolve([]));
      await this.fetchItems(action, ITEMS.ACCOUNTS, FIELDS.ACCOUNT);
      this.setFirstAccount();
    },
    scrollContainerToBottom() {
      this.$refs.container.scrollTop = 10000;
    },
    showSpinnerByFieldName(name) {
      const field = this.$refs.form?.getFieldByName(name);
      field?.showSpinner();
    },
    hideSpinnerByFieldName(name) {
      const field = this.$refs.form?.getFieldByName(name);
      field?.hideSpinner();
    },
    setFocusByFieldName(name) {
      const field = this.$refs.form?.getFieldByName(name);
      this.$nextTick(field?.focus);
    },
    setFirstAccount() {
      if (this.accounts.length > 1) return;
      const name = FIELDS.ACCOUNT;
      const [firstAccount] = this.accounts;
      this.values[name] = firstAccount;
      const field = this.$refs.form?.getFieldByName(name);
      field?.blur();
      field?.hidePopup();
    },
    clearFormMessage() {
      this.$refs.form?.setMessage('');
    },
    clearValueByName(name) {
      this.values[name] = null;
    },
    clearValues() {
      Object.keys(this.values).forEach(this.clearValueByName);
    },
    addMore() {
      this.clearValues();
      this.hideThanks();
    },
    goToPersonalScreen() {
      this.$router.push(this.$router.options.paths.personal);
    },
  },
};
</script>
